/**
 * PlanningModeView — the Planning-mode surface of the active list (T12.1.5.1.2).
 *
 * Planning is a planning list FIRST: this renders as a complete, usable plan
 * with zero offer data. Three data-driven states:
 *   - Cold start (empty list)  → the "Let's learn what you buy" view + the
 *     capture CTA + common-starter rows.
 *   - Populated                → ShoppingListItemRow per pending item (collapsed
 *     row + expandable availability), then the "Suggested for you" stub.
 *   - Sparse (items, no prices) is just the populated state with no chips — the
 *     normal baseline, handled inside each row, not a separate branch.
 *
 * Writes flow through the same orchestrator the legacy list uses
 * (`useOrchestrators().ShoppingLists`) so the T6 reducer → storage → API
 * contract stays intact:
 *   - quantity stepper        → handleUpdateItem({ quantity })
 *   - where-to-buy store pick → handleUpdateItem({ selectedRetailerOfferId })
 *
 * Inline add reuses the shell's CaptureFab (mounted by AppShell); the cold-start
 * CTA + starter rows open it via the shared `openCapture` event so there is no
 * second add flow. The Suggested section is a UI stub from an empty source.
 *
 * Shopping mode keeps the existing list rendering — this surface is Planning
 * only (ShoppingListPage routes by mode).
 *
 * All styles flow from tokens (CP5).
 */

import { useMemo, type CSSProperties } from 'react';
import {
  useActiveShoppingList,
  useHouseholdProducts,
  useSelectedStores,
  useStorePreferences,
  useStores,
} from '@shopista/shared/api/hooks';
import { apiErrorMessage } from '@shopista/shared/api';
import type { ShoppingListItemWithDetails } from '@shopista/shared/types';
import { Button, Card } from '../../components';
import { useOrchestrators } from '../../stores/orchestrator-context';
import { openCapture } from '../../layout/capture-events';
import { useBadgeSignals } from '../deals/useBadgeSignals';
import { usePreferenceBroadcastSync } from '../../lib/preferenceBroadcast';
import { ShoppingListItemRow } from './ShoppingListItemRow';
import type { StoreDealEntry } from './storeLocationOfferResolver';
import { SuggestedForYou, type Suggestion } from './SuggestedForYou';
import * as s from './planningModeStyles';

// Stable empty set for items with no linked SystemProduct id yet — avoids a new
// Set() per render (which would churn the row's match memo).
const EMPTY_IDS: ReadonlySet<string> = new Set();

const pageWrapperStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--space-2)',
};

const titleStyle: CSSProperties = {
  margin: 0,
  // T47 Phase 4 — "Plan your list" page heading → Poppins display.
  fontFamily: 'var(--font-family-display)',
  fontSize: 'var(--font-size-lg)',
  fontWeight: 'var(--font-weight-semibold)',
};

const subStyle: CSSProperties = {
  fontSize: 'var(--font-size-sm)',
  color: 'var(--color-text-muted)',
  margin: 'var(--space-1) 0 var(--space-3)',
};

// BUG50 — was `var(--space-6) var(--space-4)` (24px vertical) stacked on top
// of the wrapping Card's own padding (now `padding="none"`, see below); even
// with that double-padding gone, 24px vertical was still oversized for a
// three-line empty-state card. 16px (space-4) is the standard Card "md"
// padding elsewhere and reads just as comfortably.
const emptyCardStyle: CSSProperties = {
  textAlign: 'center',
  padding: 'var(--space-4)',
};

const emptyEmojiStyle: CSSProperties = {
  fontSize: 'var(--font-size-2xl, var(--font-size-xl))',
};

const errorStyle: CSSProperties = {
  color: 'var(--color-status-danger)',
  fontSize: 'var(--font-size-sm)',
  marginTop: 'var(--space-2)',
};

// BUG50 — padding/marginBottom tightened from space-3/space-2 (12px/8px) to
// space-2/space-1 (8px/4px). Each of the 3 rows carried its own bit of extra
// air; compounded across 3 rows that was enough on its own to push "Eggs"
// under the bottom nav at 360×780, on top of the empty-card doubling above.
// minHeight keeps the 44px (2.75rem) tap-target floor either way.
const starterRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--space-3)',
  width: '100%',
  background: 'var(--color-bg-raised)',
  borderWidth: '0.0625rem',
  borderStyle: 'solid',
  borderColor: 'var(--color-border-subtle)',
  borderRadius: 'var(--radius-lg)',
  padding: 'var(--space-2)',
  marginBottom: 'var(--space-1)',
  minHeight: '2.75rem',
  cursor: 'pointer',
  textAlign: 'left',
  color: 'inherit',
  fontFamily: 'var(--font-family-sans)',
};

// Common starters for the cold-start view — adding opens the capture sheet
// (same inline-add path as the FAB), matching the prototype's cold start.
const STARTERS = [
  { name: 'Milk', emoji: '🥛' },
  { name: 'Bread', emoji: '🍞' },
  { name: 'Eggs', emoji: '🥚' },
];

export interface PlanningModeViewProps {
  /**
   * The Suggested-for-you data source. T12.5 fills this; default empty so the
   * section renders its stub placeholder. Exposed as a prop for testability and
   * so the ranking can be wired without touching this surface.
   */
  suggestions?: Suggestion[];
}

export function PlanningModeView({
  suggestions = [],
}: PlanningModeViewProps): JSX.Element {
  const listQuery = useActiveShoppingList();
  const selectedStoresQuery = useSelectedStores();
  const storesQuery = useStores();
  // T27.2.7.9.3 — the per-store preference entries carry `is_favorite`; the
  // favourite store is the PREFERRED store the Where picker pre-highlights.
  const storePreferencesQuery = useStorePreferences();
  const productsQuery = useHouseholdProducts();
  const orchestrators = useOrchestrators();
  // T12.5.1 (G) — refresh badges when another tab changes a preference.
  usePreferenceBroadcastSync();

  // T12.5.1 (C/E) — the household's full preference signal sets, read ONCE here at
  // the list level (TD#2: no per-row preference fan-out) and threaded into every
  // row. Same hook the Deals surface uses, so the two render the SAME badge set.
  const badgeSignals = useBadgeSignals();
  // The · your brand qualifier still needs the LIKE + PREFERRED brand names.
  const likedBrandNames = useMemo(() => {
    const set = new Set(badgeSignals.likedBrandNames);
    for (const n of badgeSignals.preferredBrandNames) set.add(n);
    return set;
  }, [badgeSignals]);

  // T12.5.1 (E) — each product's linked SystemProduct id(s) so a row can match an
  // offer to its item by IDENTITY (the offer carries a resolved `systemproduct_id`
  // now). Keyed by householdproduct_id.
  const productSystemProductIds = useMemo(() => {
    const map = new Map<string, Set<string>>();
    for (const p of productsQuery.data?.products ?? []) {
      map.set(p.id, new Set(p.linked_systemproduct_ids ?? []));
    }
    return map;
  }, [productsQuery.data]);

  const items = listQuery.data?.items ?? [];
  const pendingItems = items.filter((it) => it.status === 'pending');

  // Selected stores resolved to { id, name } by cross-referencing the id-only
  // /selected-stores response against the full store list (which carries names).
  const selectedStores = useMemo(() => {
    const ids = selectedStoresQuery.data?.storeIds ?? [];
    const byId = new Map(
      (storesQuery.data?.stores ?? []).map((st) => [st.id, st.name]),
    );
    return ids.map((id) => ({ id, name: byId.get(id) ?? null }));
  }, [selectedStoresQuery.data, storesQuery.data]);

  const storesResolved =
    !selectedStoresQuery.isLoading && !storesQuery.isLoading;

  // T27.2.7.9.3 — the household's PREFERRED store (the favourite). The Where
  // picker pre-highlights it while no store is picked yet. Null when none.
  const preferredStoreId = useMemo(() => {
    const fav = (storePreferencesQuery.data?.selected ?? []).find(
      (entry) => entry.is_favorite,
    );
    return fav?.storelocation_id ?? null;
  }, [storePreferencesQuery.data]);

  const handleSetQuantity = async (
    item: ShoppingListItemWithDetails,
    quantity: number,
  ) => {
    try {
      await orchestrators.ShoppingLists.handleUpdateItem({
        listId: item.shoppinglist_id,
        itemId: item.id,
        changes: { quantity },
        request: { quantity },
      });
    } catch (err) {
      // Surface via console; the row keeps the optimistic value. A toast layer
      // is out of scope for this task.
      console.error(apiErrorMessage(err, 'Failed to update quantity'));
    }
  };

  // The per-trip note (`notes`), authored in Planning. Saves through the same
  // orchestrator path as quantity — `handleUpdateItem({ notes })` — so it
  // persists for the trip and reads back the same in Shopping mode.
  const handleSetNote = async (
    item: ShoppingListItemWithDetails,
    notes: string,
  ) => {
    try {
      await orchestrators.ShoppingLists.handleUpdateItem({
        listId: item.shoppinglist_id,
        itemId: item.id,
        changes: { notes },
        request: { notes },
      });
    } catch (err) {
      console.error(apiErrorMessage(err, 'Failed to update note'));
    }
  };

  // T27.2.7.9.3 — the where-to-buy store pick. Picking a store persists
  // `selectedStoreLocationId` (always) + `selectedRetailerOfferId` (the matched
  // offer when the chosen store has a deal, else null). Re-tapping the chosen
  // store passes `(null, null)` to clear both back to "decide later". The
  // invariant (offer never set without a store, and at the same store) is enforced
  // here, the single write path.
  //
  // T27.2.7.9.6 (reopen fix 2) — a bare store pick ALWAYS clears the pin
  // (`flyerdeal_id` + its snapshot → null), unconditionally, for BOTH deselect and
  // switch-to-a-different-store. A flyer belongs to its retailer, so a Walmart
  // flyer can never apply to an Atlantic selection — changing or picking a store
  // drops the pin, full stop. (A flyer is re-pinned through `handlePinDeal`, which
  // sets the store + pin together.)
  const handlePickStore = async (
    item: ShoppingListItemWithDetails,
    storeLocationId: string | null,
    offerId: string | null,
  ) => {
    try {
      await orchestrators.ShoppingLists.handleUpdateItem({
        listId: item.shoppinglist_id,
        itemId: item.id,
        changes: {
          selected_storelocation_id: storeLocationId,
          selected_retaileroffer_id: offerId,
          flyerdeal_id: null,
          flyerdeal_valid_from: null,
          flyerdeal_valid_to: null,
        },
        request: {
          selectedStoreLocationId: storeLocationId,
          selectedRetailerOfferId: offerId,
          flyerDealId: null,
          flyerDealValidFrom: null,
          flyerDealValidTo: null,
        },
      });
    } catch (err) {
      console.error(apiErrorMessage(err, 'Failed to save store pick'));
    }
  };

  // T27.2.7.9.6 — pin a flyer to the item (or clear it with `null`). PINNING sets
  // the chosen store + matched offer + the pin (`flyerdeal_id` + validity) in ONE
  // coherent write — a flyer belongs to its store, so pinning a Walmart deal moves
  // the chosen store to Walmart (never leaves an Atlantic pick with a Walmart pin).
  // A set pin drives the item's compact price + Trip grouping (resolved
  // server-side on the `/active` refetch). UNPINNING (`deal === null`) clears the
  // pin only and KEEPS the store + the carried offer. `retailerOfferId` is the
  // deal's store's matched offer id, threaded from the picker (or null).
  const handlePinDeal = async (
    item: ShoppingListItemWithDetails,
    deal: StoreDealEntry | null,
    retailerOfferId: string | null,
  ) => {
    const storeChange =
      deal != null
        ? {
            selected_storelocation_id: deal.storelocation_id,
            selected_retaileroffer_id: retailerOfferId,
          }
        : {};
    const storeChangeReq =
      deal != null
        ? {
            selectedStoreLocationId: deal.storelocation_id,
            selectedRetailerOfferId: retailerOfferId,
          }
        : {};
    try {
      await orchestrators.ShoppingLists.handleUpdateItem({
        listId: item.shoppinglist_id,
        itemId: item.id,
        changes: {
          ...storeChange,
          flyerdeal_id: deal?.flyerdeal_id ?? null,
          flyerdeal_valid_from: deal?.valid_from ?? null,
          flyerdeal_valid_to: deal?.valid_to ?? null,
        },
        request: {
          ...storeChangeReq,
          flyerDealId: deal?.flyerdeal_id ?? null,
          flyerDealValidFrom: deal?.valid_from ?? null,
          flyerDealValidTo: deal?.valid_to ?? null,
        },
      });
    } catch (err) {
      console.error(apiErrorMessage(err, 'Failed to pin deal'));
    }
  };

  // T27.2.7.9.6 — "Buy regular here": clear the pinned flyer (`flyerdeal_id` +
  // snapshot) AND the carried standard offer (`selected_retaileroffer_id`) while
  // KEEPING the selected store. With no offer pinned and no carried offer, the
  // compact row prices nothing (server-side). Distinct from re-tapping the store,
  // which deselects the store entirely.
  const handleRemoveOffer = async (item: ShoppingListItemWithDetails) => {
    try {
      await orchestrators.ShoppingLists.handleUpdateItem({
        listId: item.shoppinglist_id,
        itemId: item.id,
        changes: {
          selected_retaileroffer_id: null,
          flyerdeal_id: null,
          flyerdeal_valid_from: null,
          flyerdeal_valid_to: null,
        },
        request: {
          selectedRetailerOfferId: null,
          flyerDealId: null,
          flyerDealValidFrom: null,
          flyerDealValidTo: null,
        },
      });
    } catch (err) {
      console.error(apiErrorMessage(err, 'Failed to remove offer'));
    }
  };

  // Remove from the list — same orchestrator path the legacy ShoppingListPage row uses,
  // so the optimistic reducer drops the item immediately and a refetch confirms.
  const handleRemove = async (item: ShoppingListItemWithDetails) => {
    try {
      await orchestrators.ShoppingLists.handleRemoveItem({
        listId: item.shoppinglist_id,
        itemId: item.id,
      });
    } catch (err) {
      console.error(apiErrorMessage(err, 'Failed to remove item'));
    }
  };

  if (listQuery.isError) {
    return (
      <Card padding="lg" data-testid="plan-error">
        <p>Could not load your list.</p>
        <p style={errorStyle}>
          {listQuery.error instanceof Error
            ? listQuery.error.message
            : String(listQuery.error)}
        </p>
      </Card>
    );
  }

  return (
    <div style={pageWrapperStyle} data-testid="plan-page" data-mode="planning">
      <h2 style={titleStyle}>Plan your list</h2>
      <p style={subStyle}>
        Tap ＋ to add · tap an item to set how many — and where to buy, if you
        like.
      </p>

      {pendingItems.length === 0 ? (
        <div data-testid="plan-coldstart">
          {/* BUG50 — this Card previously carried `padding="lg"` (Card's own
              --space-6/24px on every side) AROUND `emptyCardStyle`, which
              independently set its own vertical padding too — the two padding
              boxes stacked (48px, not 24px, top and bottom). That doubling,
              plus the paragraph's unstyled (browser-default) margin below, is
              what pushed the "Common starters" row low enough to sit under the
              fixed bottom nav at 360×780, and made the cold-start page taller
              than one screen even at a 1280×720 desktop viewport (deals-layout's
              dead-scrollbar check). `padding="none"` leaves `emptyCardStyle`'s
              own (now single-layer, space-4) padding as the ONE layer. */}
          <Card padding="none">
            <div style={emptyCardStyle}>
              <div style={emptyEmojiStyle} aria-hidden="true">
                🛒
              </div>
              <h3 style={{ margin: 'var(--space-2) 0 var(--space-1)' }}>
                Let&apos;s learn what you buy
              </h3>
              <p
                style={{
                  color: 'var(--color-text-muted)',
                  fontSize: 'var(--font-size-sm)',
                  // BUG50 — an unstyled <p> takes the browser's default ~1em
                  // margin top+bottom (bigger than any other gap in this
                  // card); pin it to the same token scale as everything else
                  // here instead of an accidental UA default.
                  margin: 'var(--space-2) 0',
                }}
              >
                Add a few things you buy often — we&apos;ll start finding your
                brands on sale and where they&apos;re cheapest. No history
                needed.
              </p>
              <Button
                variant="primary"
                onClick={() => openCapture()}
                data-testid="plan-coldstart-add"
              >
                ＋ Add your first item
              </Button>
            </div>
          </Card>

          {/* BUG50 — tighter top/bottom margin than the shared sectionHeadStyle
              default (which SuggestedForYou also uses, populated-view-only —
              left untouched there). Scoped to this cold-start instance via a
              merged style so the shared const stays a no-op change everywhere
              else. */}
          <h3 style={{ ...s.sectionHeadStyle, margin: 'var(--space-2) var(--space-1) var(--space-1)' }}>
            Common starters · tap to add
          </h3>
          {STARTERS.map((starter) => (
            <button
              type="button"
              key={starter.name}
              style={starterRowStyle}
              onClick={() => openCapture(starter.name)}
              data-testid={`plan-starter-${starter.name.toLowerCase()}`}
            >
              <span style={s.thumbStyle} aria-hidden="true">
                {starter.emoji}
              </span>
              <span style={s.metaStyle}>
                <span style={s.nameStyle}>{starter.name}</span>
                <span style={s.qtyFlagStyle}>Add + set your brand</span>
              </span>
              <span style={s.starterAddGlyphStyle} aria-hidden="true">
                ＋
              </span>
            </button>
          ))}
        </div>
      ) : (
        <>
          {/* No inline "+ Add item" here — the floating CaptureFab ＋ covers add
              at every width (it is not hidden on desktop). The cold-start CTA
              below is the empty-state primary action. */}
          <div data-testid="plan-items">
            {pendingItems.map((item) => (
              <ShoppingListItemRow
                key={item.id}
                item={item}
                selectedStores={selectedStores}
                preferredStoreId={preferredStoreId}
                storesResolved={storesResolved}
                likedBrandNames={likedBrandNames}
                badgeSignals={badgeSignals}
                itemSystemProductIds={
                  productSystemProductIds.get(item.householdproduct_id) ?? EMPTY_IDS
                }
                onSetQuantity={handleSetQuantity}
                onSetNote={handleSetNote}
                onPickStore={handlePickStore}
                onPinDeal={handlePinDeal}
                onRemoveOffer={handleRemoveOffer}
                onRemove={handleRemove}
              />
            ))}
          </div>

          <SuggestedForYou
            suggestions={suggestions}
            onAdd={(sug) => openCapture(sug.name)}
          />
        </>
      )}
    </div>
  );
}
